import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Switch, message, Space, Divider, Tabs, Select } from 'antd';
import { FacebookOutlined, InfoCircleOutlined, AppstoreOutlined, GlobalOutlined } from '@ant-design/icons';
import { supabase } from '../../utils/supabase';
import FacebookPixelCategories from './FacebookPixelCategories';

const { TabPane } = Tabs;

const FacebookSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /**
   * Retourne la boutique sélectionnée pour isoler le Pixel et le catalogue
   * Facebook de chaque boutique e-commerce.
   * @returns {string|null} Identifiant de la boutique active.
   */
  const getActiveShopId = () => window.localStorage.getItem('activeShopId');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('global');

  useEffect(() => {
    loadFacebookSettings();
  }, []);

  // Charger les paramètres Facebook existants
  const loadFacebookSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const shopId = getActiveShopId();
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer Facebook.');

      const { data: settings, error } = await supabase
        .from('settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('shop_id', shopId)
        .eq('type', 'facebook')
        .single();

      if (settings) {
        form.setFieldsValue({
          facebook_pixel_id: settings.facebook_pixel_id,
          facebook_access_token: settings.facebook_access_token,
          facebook_test_code: settings.facebook_test_code,
          facebook_enabled: settings.facebook_enabled || false,
          whatsapp_contact_tracking_enabled: settings.whatsapp_contact_tracking_enabled || false,
          // Moment d'envoi de l'événement Purchase (défaut: à la création de la commande)
          facebook_purchase_event_timing: settings.facebook_purchase_event_timing || 'creation',
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres Facebook:', error);
      message.error('Erreur lors du chargement des paramètres Facebook');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder les paramètres Facebook
  const saveFacebookSettings = async (values) => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const shopId = getActiveShopId();
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer Facebook.');

      const { error } = await supabase
        .from('settings')
        .upsert({
          user_id: user.id,
          shop_id: shopId,
          type: 'facebook',
          facebook_pixel_id: values.facebook_pixel_id,
          facebook_access_token: values.facebook_access_token,
          facebook_test_code: values.facebook_test_code,
          facebook_enabled: values.facebook_enabled || false,
          whatsapp_contact_tracking_enabled: values.whatsapp_contact_tracking_enabled || false,
          // Moment d'envoi de l'événement Purchase pour les commandes du site
          facebook_purchase_event_timing: values.facebook_purchase_event_timing || 'creation',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,shop_id,type'
        });

      if (error) throw error;

      message.success('Paramètres Facebook sauvegardés avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres Facebook:', error);
      message.error('Erreur lors de la sauvegarde des paramètres Facebook');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card
        title={
          <div className="flex items-center space-x-2">
            <FacebookOutlined className="text-blue-600" data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} />
            <span>Configuration Facebook Pixel</span>
          </div>
        }
        className="shadow-sm"
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
        >
          <TabPane
            tab={
              <span>
                <GlobalOutlined className="mr-2" />
                Configuration globale
              </span>
            }
            key="global"
          >
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <InfoCircleOutlined className="text-blue-600 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">À propos du Facebook Pixel</p>
              <p>
                Le Facebook Pixel vous permet de suivre les conversions et d'optimiser vos publicités Facebook. 
                Configurez votre ID de pixel et votre token d'accès pour activer le tracking des événements.
              </p>
            </div>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={saveFacebookSettings}
          className="max-w-2xl"
        >
          <Form.Item
            name="facebook_enabled"
            label="Activer le tracking Facebook"
            valuePropName="checked"
            extra="Activez cette option pour commencer à suivre les événements Facebook"
          >
            <Switch />
          </Form.Item>

          <Divider />

          <Form.Item
            name="facebook_pixel_id"
            label="ID du Pixel Facebook"
            rules={[
              { required: true, message: 'Veuillez saisir l\'ID du pixel Facebook' },
              { pattern: /^\d+$/, message: 'L\'ID du pixel doit contenir uniquement des chiffres' }
            ]}
            extra="Trouvez votre ID de pixel dans le Gestionnaire d'événements Facebook"
          >
            <Input 
              placeholder="Ex: 1234567890123456"
              prefix={<FacebookOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="facebook_access_token"
            label="Token d'accès Facebook"
            rules={[
              { required: true, message: 'Veuillez saisir le token d\'accès Facebook' }
            ]}
            extra="Token d'accès pour l'API Marketing Facebook (avec permissions ads_management)"
          >
            <Input.Password 
              placeholder="Saisissez votre token d'accès Facebook"
            />
          </Form.Item>

          <Form.Item
            name="facebook_test_code"
            label="Code de test Facebook (optionnel)"
            extra="Code de test pour valider les événements en mode développement"
          >
            <Input 
              placeholder="Ex: TEST12345"
            />
          </Form.Item>

          <Divider />

          {/* Choix du moment d'envoi de l'événement Purchase pour les commandes du site.
              En paiement à la livraison, la commande n'est un achat réel qu'une fois
              confirmée par téléphone, livrée et encaissée : l'annonceur arbitre entre
              volume de signal (création) et exactitude des conversions (encaissement).
              L'événement n'est envoyé qu'une seule fois, au moment choisi. */}
          <Form.Item
            name="facebook_purchase_event_timing"
            label="Moment d'envoi de l'événement Purchase"
            initialValue="creation"
            extra="Choisissez à quel moment du cycle de commande l'achat est déclaré à Facebook. L'événement n'est envoyé qu'une seule fois par commande."
          >
            <Select
              options={[
                {
                  value: 'creation',
                  label: 'À la création de la commande (plus de volume)'
                },
                {
                  value: 'completion',
                  label: 'À l\'encaissement, commande marquée payée (données exactes)'
                }
              ]}
            />
          </Form.Item>

          <Divider />

          {/* Tracking des personnes qui écrivent sur WhatsApp : alimente les audiences
              de retargeting, ces prospects ne passant pas par le site donc pas par le pixel */}
          <Form.Item
            name="whatsapp_contact_tracking_enabled"
            label="Suivre les contacts WhatsApp"
            valuePropName="checked"
            extra="Envoie un événement Contact à Facebook au premier message d'un numéro (une fois par personne tous les 30 jours). Aucun message n'est enregistré, seul le numéro est transmis de façon chiffrée."
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={saving}
                icon={<FacebookOutlined />}
              >
                Sauvegarder les paramètres
              </Button>
              <Button 
                onClick={loadFacebookSettings}
                loading={loading}
              >
                Recharger
              </Button>
            </Space>
          </Form.Item>
        </Form>
          </TabPane>

          <TabPane
            tab={
              <span>
                <AppstoreOutlined className="mr-2" />
                Par catégorie
              </span>
            }
            key="category"
          >
            <FacebookPixelCategories />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default FacebookSettings;
